import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Collapse,
  Paper,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
  Button,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import BlockIcon from "@mui/icons-material/Block";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";

const SectionHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "12px 0",
  cursor: "pointer",
  userSelect: "none",
});

const RejectionCard = styled(Paper)({
  padding: 16,
  border: "1px solid #ef9a9a",
  backgroundColor: "#fff5f5",
  transition: "all 0.2s ease",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0px 4px 15px rgba(0,0,0,0.12)",
  },
});

const formatRespondedAt = (value) => {
  if (!value) return "--";
  const datePart = String(value).split("T")[0];
  const timePart = String(value).split("T")[1]?.slice(0, 5);
  return timePart ? `${datePart} ${timePart}` : datePart;
};

const extractList = (raw) => {
  if (Array.isArray(raw?.data)) return raw.data;
  if (Array.isArray(raw?.results)) return raw.results;
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object") {
    const k = Object.keys(raw).find((key) => Array.isArray(raw[key]));
    if (k) return raw[k];
  }
  return [];
};

const normalizeRecord = (r) => ({
  admission_id: r.admission_id,
  doctor_id: r.doctor_id || r.referring_doctor?.id,
  patient: r.patient || { name: r.patient_name, patient_id: r.patient_id },
  doctor_name:
    r.doctor_name ||
    r.referring_doctor?.name ||
    r.doctors?.admitting?.name ||
    "Unknown Doctor",
  responded_at: r.responded_at || r.rejection_date || r.updated_at || "",
  rejection_reason: r.rejection_reason || r.reason || "",
});

const RejectedPatientsForReferral = ({ onCountChange }) => {
  const [rejections, setRejections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [reasonModalOpen, setReasonModalOpen] = useState(false);
  const [reasonModalText, setReasonModalText] = useState("");

  // Keep a stable ref so fetchRejections can call it without stale closure
  const onCountChangeRef = useRef(onCountChange);
  onCountChangeRef.current = onCountChange;

  const BASE =
    process.env.REACT_APP_BACKEND_ROUTE_URL || "https://manage-dev.hlthtek.com/app/api/";

  const getHeaders = () => ({
    Authorization: `Bearer ${sessionStorage.getItem("accessToken") || ""}`,
    "APP-MODE": sessionStorage.getItem("APP_MODE") || "",
    "ORG-CODE": sessionStorage.getItem("ORG_CODE") || "",
    "USER-ACCESS-KEY": sessionStorage.getItem("USER_ACCESS_KEY") || "",
    USERNAME: sessionStorage.getItem("userName") || "",
  });

  const fetchRejections = useCallback(async () => {
    setLoading(true);
    setError(null);

    // Try candidate endpoints — stops at first that returns data
    const since = "2026-01-01";
    const candidates = [
      `${BASE}ipd_admission/my_rejections/`,
      `${BASE}ipd_admission/referred_by_me/?status=rejected&since=${since}`,
      `${BASE}ipd_admission/referred_by_me/?status=Declined&since=${since}`,
      `${BASE}ipd_admission/referred_to_me/?status=rejected&since=${since}`,
      `${BASE}ipd_admission/referred_to_me/?status=Declined&since=${since}`,
    ];

    let found = [];
    for (const url of candidates) {
      try {
        const res = await axios.get(url, { headers: getHeaders() });
        const list = extractList(res.data);
        console.log(`[RejectedPatientsForReferral] ${url} →`, res.data, "list length:", list.length);
        if (list.length > 0) {
          found = list.map(normalizeRecord);
          break;
        }
      } catch (err) {
        console.warn(`[RejectedPatientsForReferral] ${url} failed:`, err?.response?.status, err?.message);
      }
    }

    setRejections(found);
    if (onCountChangeRef.current) onCountChangeRef.current(found.length);
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [BASE]);

  useEffect(() => {
    fetchRejections();
  }, [fetchRejections]);

  return (
    <Box sx={{ mt: 3 }}>
      <SectionHeader onClick={() => setExpanded((v) => !v)}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <BlockIcon sx={{ color: "#c62828", fontSize: 20 }} />
          <Typography variant="subtitle1" fontWeight={600} color="#121959">
            Rejected Patients for Referral
          </Typography>
          <Chip
            label={rejections.length}
            size="small"
            sx={{ bgcolor: "#c62828", color: "#fff", height: 20, fontSize: "0.75rem", fontWeight: 600 }}
          />
        </Box>
        <Tooltip title="Refresh">
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); fetchRejections(); }}
            disabled={loading}
            sx={{ color: "#6b7280" }}
          >
            <RefreshIcon
              sx={{
                fontSize: 18,
                animation: loading ? "spin 1s linear infinite" : "none",
                "@keyframes spin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } },
              }}
            />
          </IconButton>
        </Tooltip>
      </SectionHeader>

      <Collapse in={expanded}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography color="error" variant="body2" sx={{ mb: 1 }}>{error}</Typography>
            <Button size="small" variant="outlined" color="error" startIcon={<RefreshIcon />} onClick={fetchRejections}>
              Retry
            </Button>
          </Box>
        ) : rejections.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography color="text.secondary" variant="body2">
              No rejected referrals at this time
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {rejections.map((item, idx) => {
              const key = `${item.admission_id}-${item.doctor_id}-${idx}`;
              const rejectionReason = (item.rejection_reason || "").trim();
              const respondedAt = formatRespondedAt(item.responded_at);

              return (
                <Grid item xs={12} sm={6} md={4} key={key} sx={{ display: "flex" }}>
                  <RejectionCard elevation={1}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.75 }}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#121959", flex: 1, pr: 1 }}>
                        {item.patient?.name || "Unknown Patient"}
                      </Typography>
                      <Chip
                        label="Rejected"
                        size="small"
                        sx={{ fontSize: "0.7rem", fontWeight: 600, flexShrink: 0, bgcolor: "#fce4ec", color: "#c62828", border: "1px solid #c62828" }}
                      />
                    </Box>

                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75 }}>
                      Patient ID: {item.patient?.patient_id || "--"}
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                      <PersonIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                      <Typography variant="caption" color="text.secondary">
                        Rejected by: {item.doctor_name || "--"}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.75 }}>
                      <CalendarTodayIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                      <Typography variant="caption" color="text.secondary">
                        Rejected: {respondedAt}
                      </Typography>
                    </Box>

                    {rejectionReason && (
                      <Typography
                        variant="caption"
                        onClick={() => { setReasonModalText(rejectionReason); setReasonModalOpen(true); }}
                        sx={{
                          display: "block",
                          cursor: "pointer",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontSize: "0.75rem",
                          color: "#444",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        Reason: {rejectionReason}
                      </Typography>
                    )}
                  </RejectionCard>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Collapse>

      <Dialog
        open={reasonModalOpen}
        onClose={() => setReasonModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1, fontWeight: 600 }}>
          Rejection Reason
          <IconButton aria-label="Close rejection reason" onClick={() => setReasonModalOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", color: "text.primary" }}>
            {reasonModalText}
          </Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default RejectedPatientsForReferral;
